import RefreshIcon from '@mui/icons-material/Refresh';
import { Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import { listInventoryManufacturersApi } from 'api/inventory';

// POM-5910 — manufacturer used to be a free-text field, which is how the
// inventory master ended up holding "Camber", "camber" and
// "CAMBER PHARMACEUTICALS" as three distinct manufacturers. The names now come
// from AppConfig (INVENTORY_CONFIG.MANUFACTURERS) via core, so the list can be
// curated without a UI release.
//
// The selected value is still submitted as a plain String — this constrains the
// input only. Existing rows keep whatever value they were created with and are
// never rewritten, which is also why the list is not used to filter or validate
// anything on read.
const ManufacturerSelect = ({ value, onChange, showRequiredError }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // Bumping this refetches. Manufacturer is required, so a transient failure
  // would otherwise leave Review association disabled for the life of the dialog with
  // no way back — the only recovery was to cancel and reopen.
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Fetched on mount rather than once per app session so a config change picked
  // up by core is reflected without a page reload. The list is small and served
  // from AppConfig, so the call is cheap.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    listInventoryManufacturersApi()
      .then((names) => {
        // AppConfig keeps them in whatever order they were entered; offer them A–Z.
        if (!cancelled) {
          setOptions(
            [...(names ?? [])].sort((left, right) =>
              String(left ?? '').localeCompare(String(right ?? ''), undefined, {
                sensitivity: 'base',
              })
            )
          );
        }
      })
      .catch(() => {
        // Say the lookup failed rather than showing an empty dropdown, which
        // would read as "no manufacturers are configured".
        if (!cancelled) {
          setOptions([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return (
    <Autocomplete
      size='small'
      fullWidth
      disabled={loading}
      options={options}
      value={value}
      onChange={(_, option) => onChange(option)}
      isOptionEqualToValue={(opt, val) => opt === val}
      noOptionsText='No manufacturers configured'
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label='Manufacturer *'
          placeholder={loading ? 'Loading…' : 'Select a manufacturer'}
          error={failed || showRequiredError}
          helperText={
            failed ? (
              <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Could not load the manufacturer list
                <Button
                  type='button'
                  size='small'
                  variant='text'
                  color='primary'
                  onClick={retry}
                  startIcon={<RefreshIcon sx={{ fontSize: '0.85rem' }} />}
                  sx={{ p: 0, minWidth: 0, fontSize: 'inherit', lineHeight: 'inherit' }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              ''
            )
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

ManufacturerSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showRequiredError: PropTypes.bool,
};
ManufacturerSelect.defaultProps = {
  value: null,
  showRequiredError: false,
};

export default ManufacturerSelect;
